import React, { useCallback, useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import IdField from '~/components/Fields/IdField';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';

type SeriesSelectionTableProps = {
    patient: DICOMPatientPresentation | undefined;
    isSeriesSelected: (patientId: string, seriesId: string) => boolean;
    toggleSeriesSelection: (
        patientId: string,
        studyInstanceUID: string,
        seriesId: string
    ) => void;
    formatDate: (date: string) => string;
    isLoading: boolean;
};

const SeriesSelectionTable = React.memo(
    ({
        patient,
        isSeriesSelected,
        toggleSeriesSelection,
        formatDate,
        isLoading,
    }: SeriesSelectionTableProps) => {
        const intl = useIntl();
        const [activeModalities, setActiveModalities] = useState<Set<string>>(
            new Set()
        );

        const allSeries = useMemo(
            () =>
                patient?.studies?.flatMap(
                    (study) =>
                        study.series?.map((series) => ({
                            ...series,
                            studyInstanceUID: study.studyInstanceUID,
                            studyDescription: study.studyDescription,
                            studyDate: study.studyDate,
                        })) || []
                ) || [],
            [patient?.studies]
        );

        // Modalities with their counts
        const modalityCounts = useMemo(() => {
            const counts = new Map<string, number>();
            for (const s of allSeries) {
                if (s.modality) {
                    counts.set(s.modality, (counts.get(s.modality) || 0) + 1);
                }
            }
            return counts;
        }, [allSeries]);

        const availableModalities = useMemo(
            () => Array.from(modalityCounts.keys()).sort(),
            [modalityCounts]
        );

        const clearModalities = useCallback(() => {
            setActiveModalities(new Set());
        }, []);

        // Filtered series based on active modality chips (empty = show all)
        const filteredSeries = useMemo(() => {
            if (activeModalities.size === 0) {
                return allSeries;
            }
            return allSeries.filter(
                (s) => s.modality && activeModalities.has(s.modality)
            );
        }, [allSeries, activeModalities]);

        const toggleModality = useCallback((modality: string) => {
            setActiveModalities((prev) => {
                const next = new Set(prev);
                if (next.has(modality)) {
                    next.delete(modality);
                } else {
                    next.add(modality);
                }
                return next;
            });
        }, []);

        const handleSelectAll = useCallback(() => {
            if (!patient?.patientID) {
                return;
            }
            for (const s of filteredSeries) {
                if (!isSeriesSelected(patient.patientID, s.seriesInstanceUID)) {
                    toggleSeriesSelection(
                        patient.patientID,
                        s.studyInstanceUID,
                        s.seriesInstanceUID
                    );
                }
            }
        }, [
            patient?.patientID,
            filteredSeries,
            isSeriesSelected,
            toggleSeriesSelection,
        ]);

        const handleDeselectAll = useCallback(() => {
            if (!patient?.patientID) {
                return;
            }
            for (const s of filteredSeries) {
                if (isSeriesSelected(patient.patientID, s.seriesInstanceUID)) {
                    toggleSeriesSelection(
                        patient.patientID,
                        s.studyInstanceUID,
                        s.seriesInstanceUID
                    );
                }
            }
        }, [
            patient?.patientID,
            filteredSeries,
            isSeriesSelected,
            toggleSeriesSelection,
        ]);

        const selectedCount = useMemo(() => {
            if (!patient?.patientID) {
                return 0;
            }
            return filteredSeries.filter((s) =>
                isSeriesSelected(patient.patientID!, s.seriesInstanceUID)
            ).length;
        }, [patient?.patientID, filteredSeries, isSeriesSelected]);

        const allVisibleSelected =
            filteredSeries.length > 0 &&
            selectedCount === filteredSeries.length;

        if (isLoading) {
            return (
                <Box
                    flex={1}
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                >
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                    >
                        <Typography>
                            <FormattedMessage id="loadingPatientStudies" />
                        </Typography>
                    </Box>
                </Box>
            );
        }

        if (!patient) {
            return (
                <Box
                    flex={1}
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                >
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                    >
                        <Typography color="text.secondary">
                            <FormattedMessage id="noPatientDataAvailable" />
                        </Typography>
                    </Box>
                </Box>
            );
        }

        return (
            <Box
                flex={1}
                overflow="hidden"
                display="flex"
                flexDirection="column"
            >
                {/* Toolbar: modality filter chips */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    pb={1}
                    flexWrap="wrap"
                    flexShrink={0}
                >
                    <Chip
                        label={intl.formatMessage({ id: 'all' })}
                        variant="tag"
                        size="small"
                        color={
                            activeModalities.size === 0 ? 'primary' : 'default'
                        }
                        onClick={clearModalities}
                    />
                    {availableModalities.map((mod) => (
                        <Chip
                            key={mod}
                            label={`${mod} (${modalityCounts.get(mod) || 0})`}
                            variant="tag"
                            size="small"
                            color={
                                activeModalities.has(mod)
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => toggleModality(mod)}
                        />
                    ))}
                </Box>

                {/* Scrollable table */}
                <TableContainer
                    component={Paper}
                    sx={{ flex: 1, overflow: 'auto' }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={allVisibleSelected}
                                        indeterminate={
                                            selectedCount > 0 &&
                                            !allVisibleSelected
                                        }
                                        onChange={() =>
                                            allVisibleSelected
                                                ? handleDeselectAll()
                                                : handleSelectAll()
                                        }
                                        disabled={filteredSeries.length === 0}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="modality" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="description" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="study" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="date" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="slices" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="seriesUID" />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSeries.length > 0 ? (
                                filteredSeries.map((series) => (
                                    <TableRow
                                        key={series.seriesInstanceUID}
                                        hover
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isSeriesSelected(
                                                    patient.patientID || '',
                                                    series.seriesInstanceUID
                                                )}
                                                onChange={() =>
                                                    toggleSeriesSelection(
                                                        patient.patientID || '',
                                                        series.studyInstanceUID,
                                                        series.seriesInstanceUID
                                                    )
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                {series.modality || '-'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                <SensitiveInfoTypography>
                                                    {series.seriesDescription || (
                                                        <FormattedMessage id="unnamedSeries" />
                                                    )}
                                                </SensitiveInfoTypography>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                <SensitiveInfoTypography>
                                                    {series.studyDescription ||
                                                        '-'}
                                                </SensitiveInfoTypography>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <CalendarToday fontSize="small" />
                                                <SensitiveInfoTypography>
                                                    {series.seriesDate
                                                        ? formatDate(
                                                              series.seriesDate
                                                          )
                                                        : series.studyDate
                                                          ? formatDate(
                                                                series.studyDate
                                                            )
                                                          : '-'}
                                                </SensitiveInfoTypography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {series.slices?.length || 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IdField
                                                id={series.seriesInstanceUID}
                                                typographyProps={{
                                                    fontFamily: 'monospace',
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            py={2}
                                        >
                                            <FormattedMessage id="noSeriesAvailable" />
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }
);
SeriesSelectionTable.displayName = 'SeriesSelectionTable';

export default SeriesSelectionTable;
